import type { FC } from 'react';
import { useEffect } from 'react';
import React, { useCallback, useReducer } from 'react';
import { Button, Divider, Stack } from '@mui/material';
import { SearchField } from '@pflegenavi/web-components';
import {
  selectFilteredSelected,
  selectLeftArrowDisabled,
  selectNonSelected,
  selectRightArrowDisabled,
} from './selectors';
import {
  NonSelectedResidents,
  SelectAllResidentsCheckbox,
  SelectedResidentsCounterHeader,
  SelectedResidentsList,
} from './ResidentsLists';
import { SelectionArrowButtons } from './SelectionArrowButtons';
import { useTranslation } from 'react-i18next';
import {
  initializeServiceProviderResidentQuickSelectionState,
  serviceProviderQuickResidentSelectionReducer,
} from './serviceProviderQuickResidentSelectionReducer';
import type { LineItem } from '../ServiceProviderReceiptBatchForm';
import { QuickResidentsSelectionLineItems } from './QuickResidentsSelectionLineItems';
import { useTaxItemsAndDefault } from '../../useDefaultTaxItem';
import { ShowAllResidents } from './ShowAllResidents';
import { QuickResidentsIndividualDateSelection } from './QuickResidentsIndividualDateSelection';

export interface ResidentType {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
}

interface ReceiptBatchQuickResidentSelectionFormProps {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  residents: ResidentType[];
  onConfirm: (
    residents: ResidentType[],
    lineItems: LineItem[],
    date?: Date
  ) => void;
  showAll: boolean;
  onChangeShowAll: (showAll: boolean) => void;
}

export const ReceiptServiceProviderBatchQuickResidentSelectionForm: FC<
  ReceiptBatchQuickResidentSelectionFormProps
> = ({ setOpen, residents, onConfirm, showAll, onChangeShowAll }) => {
  const { t } = useTranslation();

  const { defaultTaxCode } = useTaxItemsAndDefault();
  const [state, dispatch] = useReducer(
    serviceProviderQuickResidentSelectionReducer,
    residents,
    initializeServiceProviderResidentQuickSelectionState({
      taxes: defaultTaxCode ? [defaultTaxCode] : undefined,
    })
  );

  useEffect(() => {
    dispatch({
      type: 'UPDATE_RESIDENTS',
      payload: residents,
    });
  }, [residents, showAll]);

  const {
    searchTerm,
    staged,
    stagedForDeselection,
    selected,
    filteredNonSelected,
    filteredSelected,
    lineItems,
  } = state;

  const handleSearchChange = useCallback(
    (term: string) => {
      dispatch({
        type: 'SET_SEARCH_TERM',
        payload: term,
      });
    },
    [dispatch]
  );

  const handleStageResident = useCallback(
    (resident: ResidentType) => {
      dispatch({
        type: 'STAGE_RESIDENT',
        payload: resident,
      });
    },
    [dispatch]
  );

  const handleStageAllNonSelected = useCallback(() => {
    dispatch({
      type: 'STAGE_ALL_NONSELECTED',
    });
  }, [dispatch]);

  const handleUnStageResident = useCallback(
    (resident: ResidentType) => {
      dispatch({
        type: 'UNSTAGE_RESIDENT',
        payload: resident,
      });
    },
    [dispatch]
  );

  const handleUnStageAll = useCallback(() => {
    dispatch({
      type: 'UNSTAGE_ALL',
    });
  }, [dispatch]);

  const handleStageForDeselection = useCallback(
    (resident: ResidentType) => {
      dispatch({
        type: 'STAGE_FOR_DESELECTION',
        payload: resident,
      });
    },
    [dispatch]
  );

  const handleUnStageForDeselection = useCallback(
    (resident: ResidentType) => {
      dispatch({
        type: 'UNSTAGE_FOR_DESELECTION',
        payload: resident,
      });
    },
    [dispatch]
  );

  const setDate = useCallback(
    (date?: Date) => {
      dispatch({
        type: 'SET_DATE',
        payload: date,
      });
    },
    [dispatch]
  );

  const handleMoveStagedToSelected = useCallback(() => {
    dispatch({ type: 'MOVE_STAGED_TO_SELECTED' });
  }, [dispatch]);

  const handleMoveStagedForDeselectionToNonSelected = useCallback(() => {
    dispatch({ type: 'MOVE_STAGED_FOR_DESELECTION_TO_NON_SELECTED' });
  }, [dispatch]);

  const handleAddLineItem = useCallback((lineItem: LineItem) => {
    dispatch({
      type: 'ADD_LINE_ITEM',
      payload: lineItem,
    });
  }, []);

  const handleRemoveLineItem = useCallback((_internalId: string) => {
    dispatch({
      type: 'REMOVE_LINE_ITEM',
      payload: _internalId,
    });
  }, []);

  return (
    <>
      <Stack
        direction="row"
        sx={{
          gap: 5,
          justifyContent: 'space-between',
        }}
      >
        <Stack
          direction="row"
          sx={{
            gap: 4,
            justifyContent: 'space-between',
            minHeight: '70%',
            width: '65%',
          }}
        >
          <Stack
            sx={{
              gap: 1,
              width: '45%',
            }}
          >
            <SelectedResidentsCounterHeader
              title={t(
                'receipts.batch.form.resident-quick-selection.available-residents'
              )}
              count={staged.length}
            />
            <SearchField
              size="small"
              placeholder={t(
                'receipts.batch.form.resident-quick-selection.search-for-resident'
              )}
              value={searchTerm}
              onChangeValue={handleSearchChange}
            />
            <NonSelectedResidents
              filteredNonSelected={filteredNonSelected}
              staged={staged}
              handleStageResident={handleStageResident}
              handleUnStageResident={handleUnStageResident}
            />
            <Stack>
              <SelectAllResidentsCheckbox
                checkboxKey={selected.length.toString()}
                handleStageAllNonSelected={handleStageAllNonSelected}
                handleUnStageAll={handleUnStageAll}
                disabled={selectNonSelected(state).length === 0}
              />
              <ShowAllResidents
                onChangeShowAll={onChangeShowAll}
                showAll={showAll}
              />
            </Stack>
          </Stack>
          <SelectionArrowButtons
            disabledRight={selectRightArrowDisabled(state)}
            disabledLeft={selectLeftArrowDisabled(state)}
            onClickLeft={handleMoveStagedForDeselectionToNonSelected}
            onClickRight={handleMoveStagedToSelected}
          />
          <Stack
            sx={{
              gap: 1.5,
              width: '45%',
            }}
          >
            <SelectedResidentsList
              selected={selected}
              filteredSelected={filteredSelected}
              stagedForDeselection={stagedForDeselection}
              handleStageForDeselection={handleStageForDeselection}
              handleUnStageForDeselection={handleUnStageForDeselection}
            />
          </Stack>
        </Stack>
        <Divider orientation="vertical" flexItem />
        <Stack
          sx={{
            gap: 4,
            width: '30%',
          }}
        >
          <QuickResidentsIndividualDateSelection
            date={state.date}
            setDate={setDate}
          />
          <QuickResidentsSelectionLineItems
            // Filter out the first line item, which is always empty
            lineItems={state.lineItems.filter((lineItem) => lineItem.name)}
            addLineItem={handleAddLineItem}
            removeLineItem={handleRemoveLineItem}
          />
        </Stack>
      </Stack>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Button variant="outlined" onClick={() => setOpen(false)}>
          {t('receipts.batch.form.resident-quick-selection.cancel')}
        </Button>
        <Button
          disabled={selectFilteredSelected(state).length === 0}
          variant="contained"
          onClick={() => {
            onConfirm(selected, lineItems, state.date);
            setOpen(false);
          }}
        >
          {t('receipts.batch.form.resident-quick-selection.save')}
        </Button>
      </Stack>
    </>
  );
};
